import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

interface SkillView {
  id: string;
  key: string;
  name: string;
  description: string;
  engineHints: string[];
  engines: {
    engineKey: string;
    engineMaxBand: number | null;
    minBand: number;
    maxBand: number | null;
    fullRange: boolean;
  }[];
  prerequisites: { key: string; name: string }[];
}

interface NodeView {
  id: string;
  learningArea: string;
  yearLevel: string;
  strand: string;
  subStrand: string | null;
  acCode: string | null;
  description: string;
  acVerified: boolean;
  skills: SkillView[];
}

interface CurriculumView {
  totals: { nodes: number; skills: number; verified: number };
  nodes: NodeView[];
}

const YEAR_LABEL: Record<string, string> = { F: 'Prep / Foundation', '1': 'Year 1', '2': 'Year 2' };

/**
 * The curriculum map and editor.
 *
 * Every AC v9 code was seeded from a model's knowledge and is NOT authoritative
 * until a human has checked it against the official ACARA site. This screen
 * says so at the top, marks every unverified node, and is the only place in the
 * app that can tick one off.
 */
export function AdminCurriculum() {
  const qc = useQueryClient();
  const [area, setArea] = useState<'all' | 'English' | 'Mathematics'>('all');
  const [year, setYear] = useState<'all' | 'F' | '1' | '2'>('all');

  const curriculum = useQuery({
    queryKey: ['admin', 'curriculum'],
    queryFn: () => api<CurriculumView>('/admin/curriculum'),
  });

  const check = useQuery({
    queryKey: ['admin', 'curriculum', 'check'],
    queryFn: () =>
      api<{ ok: boolean; problems: { kind: string; detail: string }[]; skills: number }>(
        '/admin/curriculum/check',
      ),
  });

  const reseed = useMutation({
    mutationFn: () => api('/admin/curriculum/reseed', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'curriculum'] }),
  });

  const nodes = (curriculum.data?.nodes ?? []).filter(
    (n) => (area === 'all' || n.learningArea === area) && (year === 'all' || n.yearLevel === year),
  );
  const totals = curriculum.data?.totals;

  return (
    <div className="admin">
      <div className="admin-header">
        <div>
          <h1>Curriculum</h1>
          <p className="muted">
            {totals
              ? `${totals.skills} skills across ${totals.nodes} curriculum points · ${totals.verified} of ${totals.nodes} codes verified`
              : 'Loading…'}
          </p>
        </div>
        <button
          type="button"
          className="btn btn--outline"
          onClick={() => reseed.mutate()}
          disabled={reseed.isPending}
        >
          {reseed.isPending ? 'Refreshing…' : 'Refresh from seed'}
        </button>
      </div>

      <section className="card card--warning">
        <h2>About these codes</h2>
        <p className="muted">
          Every Australian Curriculum v9 code below was written from a model&rsquo;s knowledge and
          has <strong>not</strong> been confirmed against the official ACARA site. Nothing in this
          app presents an unverified code as authoritative. Check one, correct it if needed, then
          tick <em>Verified</em> — that tick is the only thing that makes it trustworthy, and
          nothing else in the app can set it.
        </p>
      </section>

      {check.data && !check.data.ok && (
        <section className="card">
          <h2>Graph problems</h2>
          <ul className="plain-list">
            {check.data.problems.map((p) => (
              <li key={`${p.kind}-${p.detail}`} className="error-note">
                <strong>{p.kind}</strong>: {p.detail}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="tab-row" style={{ justifyContent: 'flex-start', marginBottom: 16 }}>
        {(['all', 'English', 'Mathematics'] as const).map((a) => (
          <button
            key={a}
            type="button"
            className="chip"
            data-selected={area === a}
            onClick={() => setArea(a)}
          >
            {a === 'all' ? 'Both areas' : a}
          </button>
        ))}
        {(['all', 'F', '1', '2'] as const).map((y) => (
          <button
            key={y}
            type="button"
            className="chip"
            data-selected={year === y}
            onClick={() => setYear(y)}
          >
            {y === 'all' ? 'All years' : YEAR_LABEL[y]}
          </button>
        ))}
      </div>

      {curriculum.isPending && <p className="muted">Loading…</p>}
      {nodes.map((node) => (
        <NodeCard key={node.id} node={node} />
      ))}
    </div>
  );
}

function NodeCard({ node }: { node: NodeView }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/admin/curriculum/nodes/${node.id}`, { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'curriculum'] }),
  });

  return (
    <section className="card">
      <div className="child-head">
        <div>
          <h2>
            {node.strand}
            {node.subStrand ? ` · ${node.subStrand}` : ''}{' '}
            <span className="pill">{YEAR_LABEL[node.yearLevel] ?? node.yearLevel}</span>{' '}
            <span className="pill">{node.learningArea}</span>
          </h2>
          <p className="muted">{node.description}</p>
          <p className="muted">
            <code>{node.acCode ?? 'no code'}</code>{' '}
            {node.acVerified ? (
              <span className="pill pill--ok">Verified</span>
            ) : (
              <span className="pill pill--warn">Mapping unverified</span>
            )}
          </p>
        </div>
        <button type="button" className="btn btn--quiet" onClick={() => setOpen((v) => !v)}>
          {open ? 'Close' : `${node.skills.length} skills`}
        </button>
      </div>

      {open && (
        <div className="child-body">
          <div className="field-row">
            <label>
              AC v9 code
              <input
                defaultValue={node.acCode ?? ''}
                onBlur={(e) =>
                  e.target.value !== (node.acCode ?? '') &&
                  patch.mutate({ acCode: e.target.value || null })
                }
              />
            </label>
            <label>
              Description
              <input
                defaultValue={node.description}
                onBlur={(e) =>
                  e.target.value !== node.description &&
                  patch.mutate({ description: e.target.value })
                }
              />
            </label>
          </div>

          <label className="switch-row">
            <input
              type="checkbox"
              checked={node.acVerified}
              onChange={(e) => patch.mutate({ acVerified: e.target.checked })}
            />
            <span>
              I have checked this code against the official ACARA site
              <br />
              <small className="muted">
                Tick this only after looking it up. It is the app&rsquo;s only claim of alignment.
              </small>
            </span>
          </label>

          <h3>Skills</h3>
          <ul className="plain-list">
            {node.skills.map((skill) => (
              <li key={skill.id}>
                <strong>{skill.name}</strong> <code className="muted">{skill.key}</code>
                <br />
                <span className="muted">{skill.description}</span>
                {skill.prerequisites.length > 0 && (
                  <>
                    <br />
                    <span className="muted">
                      Needs first: {skill.prerequisites.map((p) => p.name).join(', ')}
                    </span>
                  </>
                )}
                {skill.engines.length === 0 && (
                  <>
                    <br />
                    <span className="pill pill--warn" title="No engine can generate this yet">
                      Not yet served
                    </span>{' '}
                    <span className="muted">
                      nothing can generate this skill, so it cannot be practised or claimed
                    </span>
                  </>
                )}
                {skill.engines.length > 0 && (
                  <>
                    <br />
                    <span className="muted">
                      Asks:{' '}
                      {skill.engines.map((engine, i) => (
                        <span key={engine.engineKey}>
                          {i > 0 && ', '}
                          <code>{engine.engineKey}</code>{' '}
                          {engine.fullRange ? (
                            <span className="pill pill--warn" title="Not narrowed for this skill">
                              all {engine.engineMaxBand} bands
                            </span>
                          ) : (
                            <span className="pill pill--ok">
                              bands {engine.minBand}
                              {engine.maxBand !== engine.minBand && `–${engine.maxBand}`} of{' '}
                              {engine.engineMaxBand}
                            </span>
                          )}
                        </span>
                      ))}
                    </span>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
